import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import Swal from 'sweetalert2';
import './SettingsPage.css';

// Icon Components
const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DashboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 1C0 0.45 0.45 0 1 0H6C6.55 0 7 0.45 7 1V6C7 6.55 6.55 7 6 7H1C0.45 7 0 6.55 0 6V1ZM9 1C9 0.45 9.45 0 10 0H15C15.55 0 16 0.45 16 1V6C16 6.55 15.55 7 15 7H10C9.45 7 9 6.55 9 6V1ZM0 10C0 9.45 0.45 9 1 9H6C6.55 9 7 9.45 7 10V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V10ZM9 10C9 9.45 9.45 9 10 9H15C15.55 9 16 9.45 16 10V15C16 15.55 15.55 16 15 16H10C9.45 16 9 15.55 9 15V10Z" fill="currentColor" />
  </svg>
);

const RegistrationsIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
    <path d="M0 0V16H20V0H0ZM2 2H18V14H2V2ZM4 4H16V6H4V4ZM4 8H16V10H4V8ZM4 12H12V14H4V12Z" fill="currentColor" />
  </svg>
);

const TicketIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path d="M0 2C0 1.45 0.45 1 1 1H17C17.55 1 18 1.45 18 2V4C17.45 4 17 4.45 17 5C17 5.55 17.45 6 18 6V8C18 8.55 17.55 9 17 9H1C0.45 9 0 8.55 0 8V6C0.55 6 1 5.55 1 5C1 4.45 0.55 4 0 4V2Z" fill="currentColor" />
  </svg>
);

const CheckInIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path d="M0 1V15H14V1H0ZM2 3H12V13H2V3ZM6 5H8V7H6V5ZM6 9H8V11H6V9Z" fill="currentColor" />
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 0V16H16V0H0ZM2 2H14V14H2V2ZM4 4H12V6H4V4ZM4 8H12V10H4V8ZM4 12H8V14H4V12Z" fill="currentColor" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 14H2C1.45 14 1 13.55 1 13V3C1 2.45 1.45 2 2 2H6V0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H6V14ZM11.59 10.59L9.59 12.59L11.18 14.18L15.36 10L11.18 5.82L9.59 7.41L11.59 9.41H5V11.41H11.59Z" fill="currentColor" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 12.5C11.38 12.5 12.5 11.38 12.5 10C12.5 8.62 11.38 7.5 10 7.5C8.62 7.5 7.5 8.62 7.5 10C7.5 11.38 8.62 12.5 10 12.5ZM15.92 6.42L14.5 5C14.5 3.62 13.38 2.5 12 2.5L10.58 1.08C9.2 1.08 8.08 2.2 8.08 3.58L6.66 5C5.28 5 4.16 6.12 4.16 7.5L2.74 8.92C2.74 10.3 3.86 11.42 5.24 11.42L6.66 12.84C6.66 14.22 7.78 15.34 9.16 15.34L10.58 16.76C11.96 16.76 13.08 15.64 13.08 14.26L14.5 12.84C15.88 12.84 17 11.72 17 10.34L18.42 8.92C18.42 7.54 17.3 6.42 15.92 6.42Z" fill="currentColor" />
  </svg>
);

const BroadcastIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2H18C19.1 2 20 2.9 20 4V12C20 13.1 19.1 14 18 14H2C0.9 14 0 13.1 0 12V4C0 2.9 0.9 2 2 2ZM18 4L10 8.5L2 4V4L10 0.5L18 4ZM2 6V12H18V6L10 10.5L2 6Z" fill="currentColor" />
  </svg>
);

const SaveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M15 0H3C1.35 0 0 1.35 0 3V15C0 16.65 1.35 18 3 18H15C16.65 18 18 16.65 18 15V3C18 1.35 16.65 0 15 0ZM7 15L3 11L4.41 9.59L7 12.17L13.59 5.58L15 7L7 15Z" fill="currentColor" />
  </svg>
);

function SettingsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    eventName: 'Old Skool Hangout',
    eventDate: 'December 15, 2025',
    eventTime: '6:00 PM - 11:00 PM',
    eventLocation: 'Kanu Ndubisi park, Mobolaji Johnson Ikeja'
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const currentUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        };
        setUser(currentUser);
        await fetchSettings(currentUser);
      } else {
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchSettings = async (currentUser) => {
    try {
      const settingsRef = doc(db, 'settings', 'event');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      } else {
        // Create default settings if they don't exist
        const defaultSettings = {
          eventName: 'Old Skool Hangout',
          eventDate: 'December 15, 2025',
          eventTime: '6:00 PM - 11:00 PM',
          eventLocation: 'Kanu Ndubisi park, Mobolaji Johnson Ikeja',
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || 'admin'
        };
        await setDoc(settingsRef, defaultSettings);
        setSettings(defaultSettings);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsRef = doc(db, 'settings', 'event');
      await setDoc(settingsRef, {
        ...settings,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || 'admin'
      }, { merge: true });

      await Swal.fire({
        icon: 'success',
        title: 'Settings saved',
        text: 'Your event details have been updated successfully.',
        confirmButtonColor: '#003399'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error saving settings. Please try again.',
        confirmButtonColor: '#b91c1c'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminUser');
      sessionStorage.removeItem('adminUser');
      navigate('/admin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavClick = (path) => {
    closeSidebar();
    navigate(path);
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="settings-page-container">

      </div>
    );
  }

  return (
    <div className="settings-page-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button className="sidebar-close-button" onClick={closeSidebar} aria-label="Close menu">
          <CloseIcon />
        </button>

        <div className="sidebar-logo">
          <div className="logo-icon">
            <img
              src={process.env.PUBLIC_URL + '/logo.jpg'}
              alt="Mountain Top University"
              className="admin-dashboard-logo-image"
            />
          </div>
          <div className="logo-text">
            <div className="logo-title">MTU Alumni</div>
            <div className="logo-subtitle">Event Management</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard')}>
            <DashboardIcon />
            <span>Dashboard</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/registrations')}>
            <RegistrationsIcon />
            <span>Registrations</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/generate-ticket')}>
            <TicketIcon />
            <span>Generate Ticket</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/checkin')}>
            <CheckInIcon />
            <span>Check-in</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/broadcasts')}>
            <BroadcastIcon />
            <span>Email Broadcast</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard?export=1')}>
            <ExportIcon />
            <span>Export Data</span>
          </div>
          <div className="nav-item active" onClick={closeSidebar}>
            <SettingsIcon />
            <span>Settings</span>
          </div>
        </nav>

        <div className="sidebar-user">
          <div className="user-info">
            <div className="user-avatar">
              {getUserInitials(user?.email || 'Admin User')}
            </div>
            <div className="user-details">
              <div className="user-name">Admin User</div>
              <div className="user-email">{user?.email || 'admin@mtu.edu'}</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="settings-main">
        <div className="settings-content">
          <div className="page-header">
            <div className="page-header-left">
              <h2 className="page-title">Event Settings</h2>
              <p className="page-subtitle">Manage event details and information</p>
            </div>
            <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle menu">
              <HamburgerIcon />
            </button>
          </div>

          <div className="settings-card">
            <div className="settings-header">
              <h3 className="settings-title">Event Information</h3>
              <p className="settings-subtitle">Update event details that will be displayed on the registration page and tickets</p>
            </div>

            <div className="settings-form">
              <div className="form-group">
                <label className="form-label">Event Name</label>
                <input
                  type="text"
                  name="eventName"
                  className="form-input"
                  value={settings.eventName}
                  onChange={handleInputChange}
                  placeholder="e.g., Old Skool Hangout"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Event Date</label>
                <input
                  type="text"
                  name="eventDate"
                  className="form-input"
                  value={settings.eventDate}
                  onChange={handleInputChange}
                  placeholder="e.g., December 15, 2025"
                />
                <p className="form-hint">Format: Month Day, Year (e.g., December 15, 2025)</p>
              </div>

              <div className="form-group">
                <label className="form-label">Event Time</label>
                <input
                  type="text"
                  name="eventTime"
                  className="form-input"
                  value={settings.eventTime}
                  onChange={handleInputChange}
                  placeholder="e.g., 6:00 PM - 11:00 PM"
                />
                <p className="form-hint">Format: Start Time - End Time (e.g., 6:00 PM - 11:00 PM)</p>
              </div>

              <div className="form-group">
                <label className="form-label">Event Venue</label>
                <input
                  type="text"
                  name="eventLocation"
                  className="form-input"
                  value={settings.eventLocation}
                  onChange={handleInputChange}
                  placeholder="e.g., Kanu Ndubisi park, Mobolaji Johnson Ikeja"
                />
                <p className="form-hint">Enter the full venue address</p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="save-button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <SaveIcon />
                  <span>{saving ? 'Saving...' : 'Save Settings'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SettingsPage;

